import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom'; // <-- AÑADIMOS ESTO

const navLinks = ['mission', 'workflow', 'services', 'faq', 'contact'];

export default function Header() {
  const { lang, toggleLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const navigate = useNavigate(); // <-- AÑADIMOS ESTO
  const location = useLocation(); 

  useEffect(() => {
    // OPTIMIZACIÓN DE LAG: Evita saturar el móvil al hacer scroll
    const onScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(prev => prev !== isScrolled ? isScrolled : prev);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false); 
    if (location.pathname === '/') {
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      navigate(`/#${id}`);
    }
  };

  // FUNCIÓN PARA ABRIR LA AUDITORÍA SIN RECARGAR LA PÁGININA
  const handleAuditNav = () => {
    setMobileOpen(false);
    navigate('/auditoria-gratuita');
    // Forzamos salto "duro" (auto anula el smooth) dándole un respiro a React para cargar la página
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 50);
  };

  return (
    <motion.header
      data-testid="header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-black/70 border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16 md:h-20">
        
        <a 
          href="/" 
          onClick={(e) => { 
            setMobileOpen(false);
            if (location.pathname === '/') {
              e.preventDefault(); 
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 300); 
            }
          }} 
          data-testid="logo"
        >
          <img src="/LOGO BLANCO FIRST.avif" alt="FirstStudioLogo" className="h-10 md:h-12 w-auto" />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((key) => (
            <button
              key={key}
              onClick={() => scrollTo(key)}
              data-testid={`nav-${key}`}
              className="text-sm font-body text-zinc-400 hover:text-white transition-colors duration-300 capitalize"
            >
              {t.nav[key]}
            </button>
          ))}
          <button
            onClick={handleAuditNav}
            className="text-sm font-body text-zinc-400 hover:text-white transition-colors duration-300"
          >
            Auditoría de Marca Gratuita
          </button>
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-4">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            className="text-xs font-mono tracking-wider border border-white/20 px-3 py-1.5 rounded-full text-zinc-400 hover:text-white hover:border-[#e14141] transition-all duration-300 uppercase"
          >
            {lang === 'es' ? 'EN' : 'ES'}
          </button>

          <button
            onClick={() => scrollTo('contact')}
            data-testid="header-cta"
            className="hidden md:block relative text-sm font-body text-white px-6 py-2.5 rounded-full overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.03] active:translate-y-0 active:scale-[0.98]"
            style={{
              background: 'rgba(225, 65, 65, 0.25)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(225, 65, 65, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.25), inset 0 -1px 0 rgba(0, 0, 0, 0.1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(225, 65, 65, 0.4)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(225, 65, 65, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.4), inset 0 -1px 0 rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(225, 65, 65, 0.25)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(225, 65, 65, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.25), inset 0 -1px 0 rgba(0, 0, 0, 0.1)';
            }}
          >
            <span className="absolute inset-0 rounded-full" style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)',
              pointerEvents: 'none',
            }} />
            <span className="relative z-10">{t.hero.cta}</span>
          </button>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white"
            aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden backdrop-blur-xl bg-black/90 border-t border-white/10 overflow-hidden"
            data-testid="mobile-nav"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((key) => (
                <button
                  key={key}
                  onClick={() => scrollTo(key)}
                  data-testid={`mobile-nav-${key}`}
                  className="text-left text-lg font-body text-zinc-300 hover:text-[#e14141] transition-colors"
                >
                  {t.nav[key]}
                </button>
              ))}
              <button
                onClick={handleAuditNav}
                className="text-left text-lg font-body text-zinc-300 hover:text-white transition-colors"
              >
                Auditoría de Marca Gratuita
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
